import type { IconType } from '@innovamat/glimmer-icons';
import type { ChangeEvent } from 'react';
import React from 'react';
import { Icon } from '../icon';
import { Typography } from '../typography';
import {
  DescriptiveText,
  InputContainer,
  StyledInput,
  StyledTooltip,
  Wrapper,
  WrapperButton,
} from './input.styled';

export type InputStatus = 'disabled' | 'error' | 'autofilled';

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  leftIcon?: IconType;
  rightIcon?: IconType;
  rightIconTooltip?: string;
  labelText?: string;
  descriptiveText?: string | JSX.Element;
  status?: InputStatus;
  onIconClick?: () => void;
  fill?: boolean;
  keepIconColor?: boolean;
  dataTestId?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      leftIcon,
      rightIcon,
      rightIconTooltip,
      labelText,
      descriptiveText,
      status,
      fill,
      onIconClick,
      keepIconColor,
      dataTestId,
      ...props
    },
    ref
  ) => {
    return (
      <Wrapper status={status} fill={fill} keepIconColor={keepIconColor}>
        {labelText && <Typography.Body2>{labelText}</Typography.Body2>}
        <InputContainer className="inputContainer">
          {leftIcon && <Icon icon={leftIcon} size="L" />}
          <StyledInput
            ref={ref}
            disabled={status === 'disabled' || status === 'autofilled'}
            data-testid={dataTestId}
            {...props}
          />
          {rightIcon && (
            <StyledTooltip content={rightIconTooltip}>
              <WrapperButton type="button" onClick={onIconClick}>
                <Icon icon={rightIcon} size="L" />
              </WrapperButton>
            </StyledTooltip>
          )}
        </InputContainer>
        {descriptiveText &&
          (typeof descriptiveText === 'string' ? (
            <DescriptiveText isError={status === 'error'}>
              {descriptiveText}
            </DescriptiveText>
          ) : (
            <Typography.Body3>{descriptiveText}</Typography.Body3>
          ))}
      </Wrapper>
    );
  }
);

export { Input };
